import React, { useEffect, useState, useCallback } from 'react';
import { Container, Button, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { showToast } from '../config/Alert';
import child from "../assets/images/child.png";
import editIcon from "../assets/images/editIcon.png";
import deleteIcon from "../assets/images/delete.png";
import cardIcon from "../assets/images/card.png";
import { getChildren, deleteChild } from '../services/ProfileApi';
import LoadingSpinner from './LoadingSpinner';
import defaultProfile from "../assets/images/default-profile.png";
import config from '../config/config';
import { formatDate } from '../utils/dateUtils';

const ViewChildProfile = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [childToDelete, setChildToDelete] = useState(null);

  const fetchChildren = useCallback(async () => {
    try {
      const response = await getChildren(user.id);
      const childrenData = response.data || [];

      // Check for subscription expiry logic
      const currentDate = new Date();
      childrenData.forEach((child) => {
        if (Array.isArray(child.facility_subscription) && child.facility_subscription.length > 0) {
          const subscription = child?.facility_subscription[0];
          if (subscription.end_date) {
            const endDate = new Date(subscription.end_date);
            const diffInDays = Math.ceil((endDate - currentDate) / (1000 * 60 * 60 * 24)); // Calculate difference in days
            child.subscriptionId = subscription.id;
            if (diffInDays < 0 && !child.isExpired) {
              child.isExpired = true;
              child.statusKey = 'expired';
            } else if (diffInDays <= 10 && !child.isExpiringSoon) {
              child.isExpiringSoon = true;
              child.expiringInDays = diffInDays;
              child.statusKey = 'expiringSoon';
            }
          }
        }
        if (child.payment_status === 0) {
          child.statusKey = 'nopayment';
          child.subscriptionId = 0;
        }
      });

      setChildren(childrenData);
    } catch (error) {
      showToast("Failed to fetch child details", 'error');
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    fetchChildren();
  }, [fetchChildren]);

  const handleDeleteChild = async () => {
    try {
      setLoading(true);
      await deleteChild(childToDelete);
      showToast("Deleted Successfully", 'success');
      setLoading(false);
      fetchChildren();
    } catch (error) {
      showToast("Failed to delete child!", 'error');
      setLoading(false);
    } finally {
      setShowDeleteModal(false);
    }
  };

  const handleDeleteModel = (id) => {
    setShowDeleteModal(true);
    setChildToDelete(id);
  };

  const handlePayment = (childId, statusKey, subscriptionId) => {
    navigate('/facilityPlan', { state: { childId, statusKey, subscriptionId } });
  };

  const handleEditChild = (id) => {
    navigate(`/childProfile/${id}`);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className='app-container mobile-bottom'>
      <Container className='mb-3'>
        <div className='AccountInnerMain mb-4 d-flex justify-content-between align-items-center'>
          <h6 className='mb-0'>
            <img src={child} width={20} alt='user' />
            <span className='ps-1'> Child Profile</span>
          </h6>
          {children.length > 0 && (
            <div className='text-end'>
              <Button variant="danger" className='text-white px-3 rounded-5' size='sm' onClick={() => navigate(`/childProfile`)}>
                Add New Child
              </Button>
            </div>
          )}
        </div>


        {children.length === 0 ? (
          <div className='text-center pt-5 mt-5'>
            <h4>You don't have any child</h4>
            <Button variant="danger" className='text-white rounded-5 px-3' onClick={() => navigate(`/childProfile`)}>
              <strong>Add Child</strong>
            </Button>
          </div>
        ) : (
          children.map((child, index) => (
            <div key={index} className='d-flex justify-content-between mb-3'>
              <div className='d-flex align-items-center'>
                <div className='uploadPic me-3'>
                  <div className='photo w-auto position-relative'>
                    <div className='image_profile bg_dark_blue'>
                      <img src={child.profile_pic_url ? `${config.Image_Base_URL}${child.profile_pic_url}` : defaultProfile} alt="Profile" />
                    </div>
                  </div>
                </div>
                <div>
                  <h6 className='text-danger mb-0 fw-7 fs-14'>
                    {child.child_name}
                    <span className='ms-2'>
                      {child.statusKey === 'expired' && (
                        <span className='badge bg-danger fs-8 mt-1'>Subscription Expired</span>
                      )}
                      {child.statusKey === 'expiringSoon' && (
                        <span className='badge bg-warning text-dark fs-8 mt-1'>Expiring in {child.expiringInDays} days</span>
                      )}
                      {child.statusKey === 'nopayment' && (
                        <span className='badge bg-warning text-dark fs-8 mt-1'>Subscribe by doing the Payment</span>
                      )}
                    </span>
                  </h6>
                  <p className='mb-0 fs-10'>{formatDate(child.date_of_birth)}</p>
                  <p className='mb-0 fs-8'>{child.gender}</p>

                </div>
              </div>
              <div>
                {(child.statusKey === 'expired' || child.statusKey === 'expiringSoon' || child.statusKey === 'nopayment') && (
                  <Button
                    variant='unknown'
                    size='small'
                    onClick={() => handlePayment(child.id, child.statusKey, child.subscriptionId)}
                    title={child.statusKey === 'nopayment' ? 'Pay Now' : 'Renew Now'}
                    className='p-0'
                  >
                    <img src={cardIcon} alt="payment" width={20} />
                  </Button>
                )}
                <Button variant='unknown' size='small' onClick={() => navigate(`/childProfile/${child.id}`)} className='mx-2 p-0'>
                  <img src={editIcon} alt="edit" width={16} />
                </Button>
                <Button variant='unknown' size='small' className='p-0' onClick={() => handleDeleteModel(child.id)}>
                  <img src={deleteIcon} alt="delete" width={18} />
                </Button>
              </div>
            </div>
          ))
        )}
      </Container>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className='text-center'>Are you sure you want to delete this Child?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size='sm' onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" size='sm' onClick={handleDeleteChild}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ViewChildProfile;